import type { ReRenderInputs } from '../render/ReRenderSession.js';
import {
  audioTimestampMicros,
  planAudioChunks,
  type AudioChunk,
  type AudioChunkPlan,
  type AudioChunkSource,
  type AudioEncodeConfig,
  type AudioEncoderAdapter,
  type EncodedAudioChunkLike,
  type PcmSource,
} from './AudioExport.js';
import type { MuxerAdapter } from './MuxExport.js';
import type { EncodedVideoChunkLike, VideoEncodeConfig } from './VideoExport.js';
import type {
  VideoExportProgress,
  VideoExportSession,
  VideoExportSettings,
} from './VideoExportSession.js';

/**
 * Video, audio and a container, driven as one export.
 *
 * Phase 7A ended with encoded video chunks and nowhere to put them. This adds
 * the two things that turn those chunks into a file somebody can open — the
 * song's own audio, encoded alongside, and a muxer to write both tracks — and
 * it adds them *around* the video pipeline rather than inside it.
 * `VideoExportSession` still owns the render loop, the capture, the frame
 * lifecycle and the video backpressure; none of that is touched here.
 *
 * ## Why audio is interleaved rather than appended
 *
 * The obvious arrangement is to encode all the video, then all the audio, then
 * mux. It works and it is the wrong shape: whichever track goes first has to be
 * held complete in memory while the second one is produced, and for a
 * four-minute 1080p60 export that is hundreds of megabytes of encoded video
 * waiting for a muxer that could have been consuming it all along. So the audio
 * advances a little after every frame — far enough to stay level with the video
 * timestamp, never further — and the muxer receives both tracks roughly in
 * timeline order, which is also the order it wants to write them in.
 *
 * ## The two clocks are one clock
 *
 * Video timestamps come from `frameIndex / fps` and audio timestamps from
 * `sampleIndex / sampleRate`. Both count from the same origin — the first
 * sample of the song — and neither consults a wall clock, `performance.now`,
 * `AudioContext.currentTime` or a frame callback. That is the whole of the sync
 * strategy. There is no drift to correct because there is nothing to drift
 * against: the two tracks are computed from the same timeline rather than
 * measured against a machine.
 */

export type AvExportState =
  | 'idle'
  | 'configuring'
  | 'exporting'
  | 'flushing'
  | 'finalizing'
  | 'finished'
  | 'cancelled'
  | 'failed';

export interface AvExportSettings extends VideoExportSettings {
  /** Sample frames per audio chunk. See `defaultChunkFrames`. */
  readonly audioChunkFrames?: number;
  /**
   * How many audio chunks the encoder may hold before the export waits.
   *
   * The audio equivalent of `maxPendingFrames`, and needed for the same reason:
   * an `AudioData` holds a real allocation, and a song's worth of them queued
   * against a busy encoder is a large amount of memory to discover at the end
   * of a long export.
   */
  readonly maxPendingAudio?: number;
  /**
   * How many audio chunks may be outstanding while the encoder has emitted
   * nothing. The audio twin of `maxStartupFrames`, and for the same reason.
   */
  readonly maxStartupAudio?: number;
}

export interface AvExportProgress {
  readonly state: AvExportState;
  readonly video: VideoExportProgress;
  readonly audioChunksEncoded: number;
  readonly audioChunksTotal: number;
  readonly audioFramesEncoded: number;
  readonly audioDropped: number;
  readonly audioQueueSize: number;
  readonly liveAudioChunks: number;
  readonly muxedVideoChunks: number;
  readonly muxedAudioChunks: number;
  readonly bytes: number;
  readonly error: string | null;
}

export interface AvTimings {
  /** Milliseconds spent drawing frames. */
  readonly renderMs: number;
  readonly captureMs: number;
  readonly videoEncodeMs: number;
  readonly audioEncodeMs: number;
  readonly muxMs: number;
  readonly totalMs: number;
}

/**
 * What the export decided about length.
 *
 * Reported rather than assumed, because the three numbers here are genuinely
 * different and a reader deserves to know which one won. The song's duration is
 * the authority. The video covers it, which means the frame count is whatever
 * it takes to reach or pass that instant — so the video track is up to one frame
 * longer. The audio runs to the song's duration exactly, to the nearest sample.
 */
export interface AvDurationPlan {
  readonly songDuration: number;
  readonly videoFrames: number;
  readonly videoDuration: number;
  readonly audioFrames: number;
  readonly audioDuration: number;
  /** Video minus audio, in seconds. Under one frame by construction. */
  readonly difference: number;
}

export interface AvExportOptions {
  readonly video: VideoExportSession;
  /** Absent for a video-only export. */
  readonly audio?: {
    readonly pcm: PcmSource;
    readonly source: AudioChunkSource;
    readonly encoder: AudioEncoderAdapter;
    readonly config: AudioEncodeConfig;
  };
  readonly muxer: MuxerAdapter;
  readonly now?: () => number;
  readonly yieldToHost?: () => Promise<void>;
}

export class AvExportSession {
  private readonly video: VideoExportSession;
  private readonly audio: AvExportOptions['audio'];
  private readonly muxer: MuxerAdapter;
  private readonly now: () => number;
  private readonly yieldToHost: () => Promise<void>;

  private state: AvExportState = 'idle';
  private settings: AvExportSettings | null = null;
  private error: string | null = null;
  private cancelled = false;
  private finalBytes: Uint8Array | null = null;

  private plans: AudioChunkPlan[] = [];
  private nextPlan = 0;
  private audioEncoded = 0;
  private audioFrames = 0;
  private audioDropped = 0;
  private muxedVideo = 0;
  private muxedAudio = 0;
  private byteCount = 0;

  /**
   * Serialises every write into the muxer.
   *
   * Video chunks arrive inside an awaited handler and audio chunks arrive from
   * an encoder callback that cannot be awaited, so without this the two would
   * overlap inside the muxer. One chain, and the export can wait for it at the
   * points where waiting is correct.
   */
  private writeChain: Promise<void> = Promise.resolve();
  private writeError: string | null = null;

  private audioForcedFlushes = 0;

  private readonly timing = { render: 0, capture: 0, videoEncode: 0, audioEncode: 0, mux: 0, total: 0 };

  constructor(options: AvExportOptions) {
    this.video = options.video;
    this.audio = options.audio;
    this.muxer = options.muxer;
    this.now = options.now ?? ((): number => Date.now());
    this.yieldToHost = options.yieldToHost ?? ((): Promise<void> => Promise.resolve());
  }

  get container(): string {
    return this.muxer.container;
  }

  /** The finished file, once `run` has resolved successfully. */
  get bytes(): Uint8Array | null {
    return this.finalBytes;
  }

  /**
   * Prepares the render, both encoders and the container.
   *
   * Everything that can fail before a frame is drawn fails here. An export that
   * is going to be refused by a codec, a muxer or an impossible frame size
   * should be refused while the caller is still asking for it, not two minutes
   * into a render.
   */
  async prepare(
    inputs: ReRenderInputs,
    settings: AvExportSettings,
    videoConfig: VideoEncodeConfig,
  ): Promise<AvExportProgress> {
    this.state = 'configuring';
    this.settings = settings;
    this.error = null;
    this.cancelled = false;
    this.finalBytes = null;
    this.audioEncoded = 0;
    this.audioFrames = 0;
    this.audioDropped = 0;
    this.muxedVideo = 0;
    this.muxedAudio = 0;
    this.byteCount = 0;
    this.nextPlan = 0;
    this.plans = [];
    this.writeChain = Promise.resolve();
    this.writeError = null;
    this.audioForcedFlushes = 0;

    const videoProgress = await this.video.prepare(inputs, settings, videoConfig);
    if (videoProgress.state === 'failed') {
      this.error = videoProgress.error;
      this.state = 'failed';
      return this.progress();
    }

    const audio = this.audio;
    if (audio !== undefined) {
      try {
        await audio.encoder.configure(audio.config);
      } catch (cause) {
        this.error = `audio encoder: ${message(cause)}`;
        this.state = 'failed';
        return this.progress();
      }
      this.plans = planAudioChunks(
        this.audioPlanFrames(inputs),
        settings.audioChunkFrames ?? 1024,
        audio.pcm.sampleRate,
      );
    }

    try {
      await this.muxer.start();
    } catch (cause) {
      this.error = `muxer: ${message(cause)}`;
      this.state = 'failed';
    }
    return this.progress();
  }

  /**
   * How much of the song the audio track covers.
   *
   * The song's own duration, not the PCM's. They are usually the same, but a
   * decoded file can carry a few milliseconds of encoder padding past the end of
   * the music, and a chart's duration is what every other part of this export is
   * measured against. Clamped to what actually exists so a metadata duration
   * longer than the file cannot ask for samples that are not there.
   */
  private audioPlanFrames(inputs: ReRenderInputs): number {
    const audio = this.audio;
    if (audio === undefined) return 0;
    const song = inputs.replay.song.durationSeconds;
    const wanted = Number.isFinite(song) && song > 0 ? song : audio.pcm.duration;
    return Math.min(audio.pcm.frameCount, Math.ceil(wanted * audio.pcm.sampleRate));
  }

  /** What the export decided about length, once prepared. */
  durationPlan(songDuration: number, fps: number): AvDurationPlan {
    const videoFrames = this.video.progress().totalFrames;
    const videoDuration = fps > 0 ? videoFrames / fps : 0;
    const audio = this.audio;
    const audioFrames = this.plans.length === 0
      ? 0
      : (this.plans[this.plans.length - 1] as AudioChunkPlan).startFrame +
        (this.plans[this.plans.length - 1] as AudioChunkPlan).frameCount;
    const audioDuration = audio === undefined || audio.pcm.sampleRate <= 0
      ? 0
      : audioFrames / audio.pcm.sampleRate;
    return {
      songDuration,
      videoFrames,
      videoDuration,
      audioFrames,
      audioDuration,
      difference: videoDuration - audioDuration,
    };
  }

  /**
   * Renders, encodes both tracks, and writes the file.
   *
   * The order at the end is the part worth reading twice. Both encoders hold
   * work that has been accepted and not yet emitted, and a container closed
   * before those are drained is missing the end of the song — which is why
   * `finalize` comes strictly after both flushes, and why a failure anywhere
   * before it aborts the file rather than finishing it.
   */
  async run(handlers: { readonly onProgress?: (progress: AvExportProgress) => void } = {}): Promise<AvExportProgress> {
    if (this.state === 'failed') return this.progress();
    const settings = this.settings;
    if (settings === null) {
      this.error = 'run() before prepare()';
      this.state = 'failed';
      return this.progress();
    }

    const started = this.now();
    this.state = 'exporting';

    try {
      const videoProgress = await this.video.run({
        // Audio advances to meet the video, once per frame, and the muxer is
        // drained to the same point. Bounded on both sides: the audio never runs
        // ahead of the picture, and neither track's encoded output is allowed to
        // pile up for more than a frame.
        onFrameEncoded: async (_index, timestampMicros) => {
          await this.pumpAudioTo(timestampMicros, settings);
          await this.drainWrites();
          if (this.writeError !== null) throw new Error(this.writeError);
        },
        onProgress: () => {
          handlers.onProgress?.(this.progress());
        },
      });

      this.timing.render = videoProgress.elapsedMs;

      if (videoProgress.state === 'failed') {
        this.error = videoProgress.error ?? 'video export failed';
        this.state = 'failed';
        await this.abort();
        return this.finish(started, handlers);
      }
      if (this.cancelled || videoProgress.state === 'cancelled') {
        this.state = 'cancelled';
        await this.abort();
        return this.finish(started, handlers);
      }

      // Whatever audio the last frames did not reach. A song is a fraction of a
      // frame longer than the video that covers it, and this is that fraction.
      this.state = 'flushing';
      await this.pumpAudioTo(Number.POSITIVE_INFINITY, settings);

      const audio = this.audio;
      if (audio !== undefined) {
        const audioStart = this.now();
        await audio.encoder.flush();
        this.timing.audioEncode += this.now() - audioStart;
      }

      // Everything the encoders emitted during their flushes is still queued
      // against the muxer at this point.
      await this.drainWrites();
      if (this.writeError !== null) {
        this.error = this.writeError;
        this.state = 'failed';
        await this.abort();
        return this.finish(started, handlers);
      }

      if (this.cancelled) {
        this.state = 'cancelled';
        await this.abort();
        return this.finish(started, handlers);
      }

      this.state = 'finalizing';
      const muxStart = this.now();
      this.finalBytes = await this.muxer.finalize();
      this.timing.mux += this.now() - muxStart;
      this.byteCount = this.finalBytes.byteLength;
      this.state = 'finished';
    } catch (cause) {
      this.error = message(cause);
      this.state = 'failed';
      await this.abort();
    }

    return this.finish(started, handlers);
  }

  private finish(
    started: number,
    handlers: { readonly onProgress?: (progress: AvExportProgress) => void },
  ): AvExportProgress {
    this.timing.total = this.now() - started;
    const videoTimings = this.video.timings();
    this.timing.render = videoTimings.renderMs;
    this.timing.capture = videoTimings.captureMs;
    this.timing.videoEncode = videoTimings.encodeMs;
    const progress = this.progress();
    handlers.onProgress?.(progress);
    return progress;
  }

  /**
   * Encodes audio up to a video timestamp.
   *
   * "Up to" means every chunk whose *start* is at or before the picture that has
   * just been encoded. A chunk straddling the boundary is included whole — audio
   * cannot be cut mid-chunk and the encoder works in fixed granules anyway — so
   * the audio runs at most one chunk ahead of the video, which at 1024 frames is
   * 23 ms and well inside the interleave any container tolerates.
   */
  private async pumpAudioTo(videoTimestampMicros: number, settings: AvExportSettings): Promise<void> {
    const audio = this.audio;
    if (audio === undefined) return;
    const maxPending = Math.max(1, settings.maxPendingAudio ?? 32);
    const maxStartup = Math.max(maxPending, settings.maxStartupAudio ?? maxPending);

    while (this.nextPlan < this.plans.length && !this.cancelled) {
      const plan = this.plans[this.nextPlan] as AudioChunkPlan;
      if (plan.timestampMicros > videoTimestampMicros) break;

      await this.drainAudioTo(maxPending, maxStartup);
      if (this.cancelled) return;

      const chunk = audio.source.take(plan.index, plan.startFrame, plan.frameCount);
      if (chunk === null) {
        this.audioDropped += 1;
        this.nextPlan += 1;
        continue;
      }

      const encodeStart = this.now();
      try {
        audio.encoder.encode(chunk);
        this.audioEncoded += 1;
        this.audioFrames += chunk.frameCount;
      } catch (cause) {
        chunk.close();
        this.timing.audioEncode += this.now() - encodeStart;
        throw new Error(`audio encode: ${message(cause)}`);
      }
      this.timing.audioEncode += this.now() - encodeStart;
      // Released whatever happened. An `AudioData` that failed to encode leaks
      // exactly as one that succeeded would.
      chunk.close();
      this.nextPlan += 1;
    }
  }

  /**
   * See `VideoExportSession.drainTo`. Same two limits, same flush, same reason.
   */
  private async drainAudioTo(limit: number, startupLimit = limit): Promise<void> {
    const audio = this.audio;
    if (audio === undefined) return;
    let waits = 0;
    let flushedThisWait = false;
    for (;;) {
      if (this.cancelled) return;
      const produced = audio.encoder.producedChunks;
      const queue = audio.encoder.queueSize;
      const bound = produced === 0 ? startupLimit : limit;
      if (queue < bound) return;

      if (!flushedThisWait && this.audioForcedFlushes < 64) {
        flushedThisWait = true;
        this.audioForcedFlushes += 1;
        await audio.encoder.flush();
        continue;
      }

      await this.yieldToHost();
      waits += 1;
      if (waits > 10_000) {
        throw new Error(
          `audio encoder did not drain below ${bound} chunks after a flush ` +
          `(queue ${queue}, chunks produced ${audio.encoder.producedChunks ?? 'unknown'})`,
        );
      }
    }
  }

  // -------------------------------------------------------------------------
  // Muxer writes
  // -------------------------------------------------------------------------

  /**
   * Receives a chunk from the host's video encoder callback.
   *
   * Not awaited by the caller — a WebCodecs output callback has nowhere to put a
   * promise — so it joins the write chain, and the export waits for that chain
   * once per frame. That is where the muxer's backpressure is applied: a slow
   * container slows the render loop instead of accumulating behind it.
   */
  acceptVideoChunk(chunk: EncodedVideoChunkLike): void {
    // Counted on the video session too, so its own progress keeps reporting
    // chunks and bytes. Without this every A/V export shows zero for both while
    // happily writing a file, which makes the diagnostics lie about the one
    // thing they exist to show.
    this.video.acceptChunk(chunk);
    this.enqueue(async () => {
      const start = this.now();
      await this.muxer.addVideoChunk(chunk);
      this.timing.mux += this.now() - start;
      this.muxedVideo += 1;
    });
  }

  /** The same, for audio. */
  acceptAudioChunk(chunk: EncodedAudioChunkLike): void {
    this.enqueue(async () => {
      const start = this.now();
      await this.muxer.addAudioChunk(chunk);
      this.timing.mux += this.now() - start;
      this.muxedAudio += 1;
    });
  }

  private enqueue(work: () => Promise<void>): void {
    this.writeChain = this.writeChain.then(work).catch((cause: unknown) => {
      // Recorded, not rethrown: an unhandled rejection on this chain would
      // escape the export entirely, and the run loop checks `writeError`.
      this.writeError ??= `muxer: ${message(cause)}`;
    });
  }

  private async drainWrites(): Promise<void> {
    await this.writeChain;
  }

  // -------------------------------------------------------------------------
  // Ending
  // -------------------------------------------------------------------------

  /**
   * Stops the export.
   *
   * Cancellation has to reach four things — the render loop, both encoders and
   * the container — and the container is the one that matters most. A file whose
   * index has been written opens cleanly and plays a truncated song, which is
   * worse than no file at all because nothing about it looks wrong.
   */
  cancel(): void {
    this.cancelled = true;
    this.video.cancel();
  }

  private async abort(): Promise<void> {
    try {
      await this.muxer.abort();
    } catch {
      // Abort is best-effort by definition; the file is being discarded.
    }
    this.finalBytes = null;
  }

  timings(): AvTimings {
    return {
      renderMs: this.timing.render,
      captureMs: this.timing.capture,
      videoEncodeMs: this.timing.videoEncode,
      audioEncodeMs: this.timing.audioEncode,
      muxMs: this.timing.mux,
      totalMs: this.timing.total,
    };
  }

  /** Audio chunk boundaries, for the sync and determinism checks. */
  audioPlan(): readonly AudioChunkPlan[] {
    return this.plans;
  }

  /** The timestamp of a sample position, exposed so a test can assert the rule. */
  audioTimestamp(sampleIndex: number): number {
    const audio = this.audio;
    if (audio === undefined) return 0;
    return audioTimestampMicros(sampleIndex, audio.pcm.sampleRate);
  }

  progress(): AvExportProgress {
    const video = this.video.progress();
    return {
      state: this.state,
      video,
      audioChunksEncoded: this.audioEncoded,
      audioChunksTotal: this.plans.length,
      audioFramesEncoded: this.audioFrames,
      audioDropped: this.audioDropped,
      audioQueueSize: this.audio?.encoder.queueSize ?? 0,
      liveAudioChunks: this.audio?.source.liveChunks ?? 0,
      muxedVideoChunks: this.muxedVideo,
      muxedAudioChunks: this.muxedAudio,
      bytes: this.byteCount,
      error: this.error ?? this.writeError,
    };
  }

  /** Releases both encoders, the capture path, the chunk source and the muxer. */
  dispose(): void {
    this.video.dispose();
    const audio = this.audio;
    if (audio !== undefined) {
      try {
        audio.encoder.close();
      } catch {
        // Already closed, or closed by the error path.
      }
      audio.source.dispose();
    }
    this.muxer.dispose();
  }
}

function message(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

/** Re-exported so a caller can build a chunk plan without reaching past this file. */
export type { AudioChunk, AudioChunkPlan };
